import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, Trash2, Users, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";

export default function CustomersTab({ onChanged }) {
  const [customers, setCustomers] = useState([]);
  const [name, setName] = useState("");
  const [firm, setFirm] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const filtered = customers.filter((c) =>
    `${c.name} ${c.firm || ""} ${c.mobile}`.toLowerCase().includes(search.toLowerCase())
  );

  const saveEdit = async () => {
    if (!editing.name.trim() || !editing.mobile.trim()) return toast.error("Name and Mobile are required");
    try {
      await api.put(`/customers/${editing.id}`, { name: editing.name, firm: editing.firm, mobile: editing.mobile });
      toast.success("Customer updated");
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/customers", { name, firm, mobile });
      toast.success(`${name} added`);
      setName(""); setFirm(""); setMobile("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add customer");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, cname) => {
    if (!window.confirm(`Delete ${cname}?`)) return;
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    load();
    onChanged?.();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="customers-tab">
      <div className="bg-card border border-border rounded-xl shadow-sm p-5 h-fit">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-cyan-600" /> New Customer
        </h3>
        <form onSubmit={add} className="mt-4 space-y-3">
          <Input placeholder="Customer Name *" value={name} onChange={(e) => setName(e.target.value)} required data-testid="customer-name-input" />
          <Input placeholder="Contact Person" value={firm} onChange={(e) => setFirm(e.target.value)} data-testid="customer-firm-input" />
          <Input placeholder="Mobile number *" type="tel" inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value)} required data-testid="customer-mobile-input" />
          <Button type="submit" disabled={busy} data-testid="customer-add-btn" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
            {busy ? "Adding..." : "Add Customer"}
          </Button>
        </form>
      </div>

      <div className="lg:col-span-2 bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <Users className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <p className="font-heading font-semibold text-sm whitespace-nowrap">{customers.length} Customer</p>
          <div className="relative flex-1 max-w-xs ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search (name / contact / mobile)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs"
              data-testid="customer-search-input"
            />
          </div>
        </div>
        {loading ? (
          <Loading text="Loading customers..." />
        ) : filtered.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground text-sm" data-testid="customers-empty">
            {search ? "No customer found for this search." : "No customers yet. Add your first customer using the form on the left."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customer-list-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Mobile</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Orders</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Total Amount</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Received</th>
                  <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Due</th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`customer-row-${c.id.slice(0, 8)}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{c.name}</p>
                      {c.firm && <p className="text-xs text-muted-foreground">{c.firm}</p>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.mobile}</td>
                    <td className="px-4 py-3 text-right">{c.order_count}</td>
                    <td className="px-4 py-3 text-right font-semibold">{inr(c.total_amount)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600">{inr(c.received_amount)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-500">{inr(c.due_amount)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...c })} data-testid={`customer-edit-btn-${c.id.slice(0, 8)}`} className="text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id, c.name)} data-testid={`customer-delete-btn-${c.id.slice(0, 8)}`} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="customer-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Customer</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Customer Name *" data-testid="customer-edit-name-input" />
              <Input value={editing.firm} onChange={(e) => setEditing({ ...editing, firm: e.target.value })} placeholder="Contact Person" data-testid="customer-edit-firm-input" />
              <Input value={editing.mobile} onChange={(e) => setEditing({ ...editing, mobile: e.target.value })} placeholder="Mobile number *" data-testid="customer-edit-mobile-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} data-testid="customer-edit-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
